import API from '../api';
import extractFormDataFromObject from '../helpers/FormDataHelper';
import UserForm from '../models/form/UserForm';
import UserTableView from '../models/view/UserTableView';
import UserView from '../models/view/UserView';
import UserInformationView from '../models/form/UserInformationView';
import DayBookingView from '../models/view/DayBookingView';
import SelectView from '../models/view/SelectView.js';
import {useUserStore} from '../../stores/userStore';
import {useLocationsStore} from '../../stores/locationsStore';
import {userPreferences} from '../models/user/userPreferences';
import {parseZodSchema} from '../models/parseZodSchema';

export default () => ({
    browse() {
        return API.get('/api/user')
            .then(API.handle)
            .catch(API.error)
            .then(json => new UserForm(json));
    },
    read(userId, accountId) {
        return API.get(`/api/admin/account/${accountId}/user/${userId}`)
            .then(API.handle)
            .catch(API.error)
            .then(json => new UserInformationView(json));
    },
    getManagers(accountId) {
        return API.get(`/api/admin/account/${accountId}/managers`)
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(json => new SelectView(json)));
    },
    getAnalysts(accountId) {
        return API.get(`/api/admin/account/${accountId}/analysts`)
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(json => new SelectView(json)));
    },
    /**
     *
     * @param {string} accountId
     * @param {string | null | undefined} query
     * @param {number | null |null} limit
     */
    fetch(accountId, query = null, limit = null) {
        const searchParams = new URLSearchParams();

        searchParams.append('limit', limit || '10');

        if (query?.trim()) {
            searchParams.append('name', query);
        }

        return API.get(`/api/admin/users/${accountId}?${searchParams.toString()}`)
            .then(API.handle)
            .catch(API.error)
            .then(raw =>
                raw.data.map(singleRaw => ({
                    id: singleRaw.id,
                    name: `${singleRaw.firstName} ${singleRaw.lastName}`,
                }))
            );
    },
    filter(account, role, license, name, offset, limit) {
        let url = '/api/admin/users';
        const searchParams = new URLSearchParams();

        if (account) {
            url = '/api/admin/users/' + account.id;
        }

        if (role) {
            searchParams.append('role', role);
        }

        if (name) {
            searchParams.append('name', name);
        }

        if (offset) {
            searchParams.append('offset', offset);
        }

        if (limit) {
            searchParams.append('limit', limit);
        }

        if (license !== null) {
            searchParams.append('licenseActive', license ? '1' : '0');
        }

        return API.get(`${url}?${searchParams.toString()}`)
            .then(API.handle)
            .catch(API.error)
            .then(raw => {
                return {
                    data: raw.data.map(singleRaw => new UserTableView(singleRaw)),
                    count: raw.totalCount,
                };
            });
    },
    edit(userId, data, accountId) {
        return API.postJSON(`/api/admin/account/${accountId}/user/${userId}`, data)
            .then(API.handle)
            .catch(API.error);
    },
    add(user, accountId) {
        return API.postJSON(`/api/admin/account/${accountId}/user/new`, user)
            .then(API.handle)
            .catch(API.error);
    },
    delete(user) {
        return API.delete(`/api/admin/user/${user.id}`).then(API.handle).catch(API.error);
    },
    confirmSso(userId) {
        return API.put(`/api/admin/user/verify-sso/${userId}`).then(API.handle).catch(API.error);
    },
    getSelectableUsersBookingForUser(searchString, userId) {
        let url = '/api/user/usersBookingForUser/selectable';

        const {user} = useUserStore();
        const {availableLocations} = useLocationsStore();

        const currentLocation = user?.primaryRoom?.location || availableLocations[0]?.id;
        const searchParams = new URLSearchParams();

        if (userId !== undefined) {
            url = `/api/user/usersBookingForUser/selectable/${userId}`;
        }

        if (searchString !== null) {
            searchParams.append('name', searchString);
        }

        if (currentLocation !== null) {
            searchParams.append('location', currentLocation);
        }

        return API.get(`${url}?${searchParams.toString()}`)
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(singleRaw => new UserView(singleRaw)));
    },
    getSelectableUsersBookingForNewUser(searchString, accountId) {
        const searchParams = new URLSearchParams();

        searchParams.append('name', searchString);

        return API.get(
            `/api/user/usersBookingForNewUser/selectable/${accountId}?${searchParams.toString()}`
        )
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(singleRaw => new UserView(singleRaw)));
    },
    getUsersBookingForUser(userId) {
        let url = '/api/user/usersBookingForUser';

        if (userId !== undefined) {
            url = `/api/user/usersBookingForUser/${userId}`;
        }

        return API.get(url)
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(singleRaw => new UserView(singleRaw)));
    },
    replaceUsersBookingForUser(userList, userId) {
        let url = '/api/user/usersBookingForUser/replace';

        if (userId !== undefined) {
            url = `/api/user/usersBookingForUser/replace/${userId}`;
        }

        const formatData = new FormData();

        userList.forEach((user, index) => {
            formatData.set(
                `users_for_other_booking[usersForOtherBooking][${index}][user]`,
                user.id
            );
        });

        return API.post(url, formatData)
            .then(response => API.handle(response, true))
            .catch(API.error);
    },
    addUsersBookingForMe(userList, userEmail) {
        const searchParams = new URLSearchParams();
        const formatData = new FormData();

        userList.forEach((user, index) => {
            formatData.set(
                `users_for_other_booking[usersForOtherBooking][${index}][user]`,
                user.id
            );
        });

        if (userEmail !== undefined) {
            searchParams.append('userEmail', userEmail);
        }

        return API.post(`/api/user/usersBookingForMe/add?${searchParams.toString()}`, formatData)
            .then(response => API.handle(response, true))
            .catch(API.error);
    },
    removeUsersBookingForMe(userList) {
        const formatData = new FormData();

        userList.forEach((user, index) => {
            formatData.set(
                `users_for_other_booking[usersForOtherBooking][${index}][user]`,
                user.id
            );
        });

        return API.post('/api/user/usersBookingForMe/remove', formatData)
            .then(response => API.handle(response, true))
            .catch(API.error);
    },
    getUsersMeBookingForAllowed() {
        return API.get('/api/user/usersMeBookingForAllowed')
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(singleRaw => new UserView(singleRaw)));
    },
    userBulkRegistration(file) {
        const formData = extractFormDataFromObject(file, 'user_import');

        return API.post('/api/admin/user/bulkregistration', formData)
            .then(API.handle)
            .catch(API.error);
    },
    downloadCsv(account, role, name, licenseActive) {
        const searchParams = new URLSearchParams();

        if ((account && typeof account === 'string') || account instanceof String) {
            searchParams.append('account', account);
        }

        if (role) {
            searchParams.append('role', role);
        }

        if (name) {
            searchParams.append('name', name);
        }

        if (licenseActive !== 'undefined' && licenseActive !== null) {
            searchParams.append('licenseActive', licenseActive);
        }

        return API.get(`/api/admin/user/csv_template?${searchParams}`)
            .then(API.handle)
            .catch(API.error)
            .then(data => {
                if (typeof data.success !== 'undefined' && data.success) {
                    return false;
                }
                const blob = new Blob([data], {type: 'text/csv;charset=utf-8'});
                const url = window.URL.createObjectURL(blob);
                const aLink = document.createElement('a');
                aLink.style.display = 'none';
                aLink.href = url;
                aLink.download = 'UserImportTemplate.csv';
                document.body.appendChild(aLink);
                aLink.click();
            });
    },
    updatePrimaryRoom(roomId) {
        const formData = new FormData();

        formData.set('primary_room[room]', roomId);

        return API.post('/api/user/update-primary-room', formData)
            .then(API.handle)
            .catch(API.error);
    },
    updateTimezone(timezone) {
        const formData = new FormData();

        formData.set('timezone[timezone]', timezone);

        return API.post('/api/user/update-timezone', formData).then(API.handle).catch(API.error);
    },
    resetFailedLoginAttempts(userId) {
        return API.put(`/api/admin/reset-login-attempts/${userId}`)
            .then(API.handle)
            .catch(API.error);
    },
    getUsersTodaysDayBookings() {
        return API.get('/api/todays-day-bookings')
            .then(API.handle)
            .catch(API.error)
            .then(raw => raw.map(singleRaw => new DayBookingView(singleRaw)));
    },
    /**
     *
     * @param {Partial<import('../models/user/userPreferences').UserPreferences>} userPreferences
     * @returns {Promise<void>}
     */
    updatePreferences(userPreferences) {
        return API.patch('/api/user/preferences', userPreferences)
            .then(API.handle)
            .catch(API.error);
    },
    getPreferences() {
        return API.get('/api/user/preferences')
            .then(API.handle)
            .catch(API.error)
            .then(raw => parseZodSchema(raw, userPreferences));
    },
});
